import React, { useState, useRef, useEffect } from 'react';
import { run, isAbortError, PARSE_WORKER_MIN_CHARS } from '../lib/worker-pool';
import { parseGeoText } from '../lib/geo-parsers';
import { runStgQc } from '../lib/stg-qc';
import { analyzeTabularFile } from '../lib/auto-detect';
import { buildXYZGeoJSON, describeSkipped } from '../lib/xyz-geojson';
import ColumnMappingDialog, { mappingStorageKey } from './ColumnMappingDialog';
import { useTheme } from '../contexts/ThemeContext';

const TABULAR_EXTS = ['csv', 'txt', 'dat', 'xyz'];

const SUPPORTED_FORMATS = [
  { ext: '.geojson,.json', label: 'GeoJSON', icon: '🌐', desc: 'GeoJSON FeatureCollection' },
  { ext: '.kml', label: 'KML', icon: '📌', desc: 'Google Earth KML' },
  { ext: '.kmz', label: 'KMZ', icon: '📦', desc: 'Compressed KML' },
  { ext: '.gpx', label: 'GPX', icon: '🧭', desc: 'GPS Exchange Format' },
  { ext: '.csv', label: 'CSV / XYZ', icon: '📊', desc: 'Tabular data — columns auto-detected' },
  { ext: '.txt,.dat,.xyz', label: 'TXT / DAT', icon: '🔢', desc: 'Delimited XYZ data' },
  { ext: '.shp', label: 'Shapefile', icon: '📐', desc: 'ESRI Shapefile (.shp + .dbf + .shx)' },
  { ext: '.dxf', label: 'DXF', icon: '📏', desc: 'AutoCAD DXF' },
  { ext: '.tif,.tiff', label: 'GeoTIFF', icon: '🗺️', desc: 'Raster GeoTIFF' },
  { ext: '.stg', label: 'SuperSting .stg', icon: '⚡', desc: 'Resistivity field data (QC only)' },
];

const TIER_COLORS = { A: '#34d399', B: '#a3e635', C: '#fbbf24', D: '#f87171' };

function downloadText(name, text) {
  const blob = new Blob([text], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * FileImportDialog — import geospatial files into the map
 *
 * Props:
 *   onImport(geojson, name, type) - callback with imported data
 *   onClose() - close dialog
 */
export default function FileImportDialog({ onImport, onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tabular, setTabular] = useState(null);   // { headers, rows, detection } for CSV/TXT/DAT/XYZ
  const [mapping, setMapping] = useState(null);   // confirmed/auto column mapping
  const [mappingSource, setMappingSource] = useState(null); // 'auto' | 'saved' | 'manual'
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [mappingError, setMappingError] = useState(null); // why an auto/saved mapping could not plot
  const [progress, setProgress] = useState(0);
  const [stgQc, setStgQc] = useState(null);       // { loading, error, result } for a chosen .stg
  const [dropC, setDropC] = useState(false);      // also drop tier C from the cleaned .stg
  const fileInputRef = useRef(null);
  const aliveRef = useRef(true);   // false after unmount: skip state updates from awaited work
  const abortRef = useRef(null);   // AbortController of the in-flight parse

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Survey QC for SuperSting .stg: tier readings A-D in the worker pool (big files) or inline.
  const runStgQcFor = async (f) => {
    setStgQc({ loading: true, error: null, result: null });
    try {
      const text = await f.text();
      if (!aliveRef.current) return;
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const result = text.length > PARSE_WORKER_MIN_CHARS
        ? await run('stgQc', { text }, { label: `Survey QC ${f.name}`, signal: abortRef.current.signal })
        : runStgQc(text);
      if (aliveRef.current) setStgQc({ loading: false, error: null, result });
    } catch (err) {
      if (aliveRef.current && !isAbortError(err)) setStgQc({ loading: false, error: err.message, result: null });
    }
  };

  const analyzeFile = async (f) => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext === 'stg') { setTabular(null); setMapping(null); runStgQcFor(f); return; }
    setStgQc(null);
    if (!TABULAR_EXTS.includes(ext)) { setTabular(null); setMapping(null); return; }
    try {
      const text = await f.text();
      const analyzed = analyzeTabularFile(text, f.name);
      setTabular(analyzed);
      setMappingError(null);
      // V.42: a header-only or empty file has nothing to map
      if (analyzed.rows.length === 0) {
        setMapping(null);
        setMappingSource(null);
        setError(analyzed.headers.length ? 'The file has no data rows' : 'The file is empty');
        return;
      }
      // Remembered mapping for this header signature?
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(mappingStorageKey(analyzed.headers)) || 'null'); } catch { /* ignore */ }
      if (saved && saved.x >= 0 && saved.y >= 0) {
        setMapping(saved);
        setMappingSource('saved');
      } else if (analyzed.detection.confidence >= 0.8 && analyzed.detection.x >= 0 && analyzed.detection.y >= 0 && analyzed.detection.z.length > 0 && analyzed.detection.crs) {
        setMapping({
          x: analyzed.detection.x, y: analyzed.detection.y,
          z: analyzed.detection.z.slice(0, 4), activeZ: analyzed.detection.z[0],
          crs: analyzed.detection.crs, format: analyzed.detection.crs === 'EPSG:4326' ? 'dd' : 'utm',
          extras: {}, headers: analyzed.headers,
        });
        setMappingSource('auto');
      } else {
        setMapping(null);
        setMappingSource(null);
      }
    } catch (err) {
      setTabular(null);
      setMapping(null);
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setError(null);
    setPreview(null);
    analyzeFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) {
      setFile(f);
      setError(null);
      setPreview(null);
      analyzeFile(f);
    }
  };

  const finishImport = (geojson, name, ext) => {
    if (!geojson || !geojson.features || geojson.features.length === 0) {
      throw new Error('No features found in the imported file');
    }
    setPreview({ count: geojson.features.length, types: countGeometryTypes(geojson), skipped: describeSkipped(geojson.metadata?.xyz) });
    if (onImport) onImport(geojson, name, ext);
  };

  const importWithMapping = (m, built = null) => {
    const name = file.name.replace(/\.[^/.]+$/, '');
    const geojson = built || buildXYZGeoJSON(tabular.headers, tabular.rows, m);
    finishImport(geojson, name, 'xyz');
  };

  const isStg = !!file && /\.stg$/i.test(file.name);
  const canImport = !!file && !loading && !isStg;

  const handleImport = async () => {
    if (!file) return;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    setError(null);

    try {
      const ext = file.name.split('.').pop().toLowerCase();
      let geojson = null;
      let name = file.name.replace(/\.[^/.]+$/, '');

      // Tabular XYZ pipeline
      if (TABULAR_EXTS.includes(ext)) {
        if (!tabular) throw new Error('Could not parse the file as tabular data');
        if (tabular.rows.length === 0) throw new Error(tabular.headers.length ? 'The file has no data rows' : 'The file is empty');
        if (mapping) {
          try {
            importWithMapping(mapping);
          } catch (err) {
            // The auto/remembered mapping does not plot (e.g. no UTM zone): fix it in the editor
            setMappingError(err.message);
            setShowMappingDialog(true);
          }
        } else {
          // Low confidence — open the mapping editor instead
          setShowMappingDialog(true);
        }
        setLoading(false);
        return;
      }

      switch (ext) {
        case 'geojson':
        case 'json':
        case 'kml':
        case 'gpx': {
          const text = await file.text();
          if (!aliveRef.current) return;
          // Big files are parsed in the worker pool (progress + Cancel in TaskConsole);
          // small ones are cheap enough to parse inline with the same pure function.
          geojson = text.length > PARSE_WORKER_MIN_CHARS
            ? await run('parse', { format: ext, text }, {
              label: `Import ${file.name}`,
              signal: abortRef.current.signal,
              onProgress: (v) => { if (aliveRef.current) setProgress(v); },
            })
            : parseGeoText(ext, text);
          if (!aliveRef.current) return;
          break;
        }
        case 'stg':
          throw new Error('SuperSting .stg files are QC-only here: review Survey QC above and download the cleaned .stg.');
        default:
          throw new Error(`Format .${ext} is not yet supported for browser import. Use GeoJSON, KML, GPX, CSV, or XYZ text.`);
      }

      finishImport(geojson, name, ext);
    } catch (err) {
      if (aliveRef.current && !isAbortError(err)) setError(err.message);
    } finally {
      if (aliveRef.current) { setLoading(false); setProgress(0); }
    }
  };

  const headerName = (i) => tabular?.headers?.[i] ?? '?';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 480, background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text0 }}>📂 Import Data</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        {/* Supported formats */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', marginBottom: 8 }}>Supported Formats</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SUPPORTED_FORMATS.map(f => (
              <span key={f.label} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, background: C.bg4, color: C.text2, border: `1px solid ${C.border}` }}>
                {f.icon} {f.label}
              </span>
            ))}
          </div>
        </div>

        {/* Drop zone */}
        <div style={{ padding: '16px 20px' }}
          onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
          <div style={{
            border: `2px dashed ${file ? C.accent : C.border}`, borderRadius: 10, padding: '24px 16px',
            textAlign: 'center', cursor: 'pointer', transition: 'all .2s',
            background: file ? C.accentBg : 'transparent',
          }} onClick={() => fileInputRef.current?.click()}>
            <input ref={fileInputRef} type="file"
              accept=".geojson,.json,.kml,.kmz,.gpx,.csv,.txt,.dat,.xyz,.shp,.dxf,.tif,.tiff,.stg"
              onChange={handleFileSelect} style={{ display: 'none' }} />
            {file ? (
              <div>
                <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text0 }}>{file.name}</div>
                <div style={{ fontSize: 11, color: C.text3 }}>{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 24, marginBottom: 8 }}>📂</div>
                <div style={{ fontSize: 12, color: C.text1 }}>Drop a file here or click to browse</div>
                <div style={{ fontSize: 10, color: C.text2, marginTop: 4 }}>GeoJSON, KML, GPX, CSV, Shapefile</div>
              </div>
            )}
          </div>
        </div>

        {/* XYZ auto-detect banner */}
        {tabular && tabular.rows.length > 0 && (
          <div style={{ padding: '0 20px 12px' }}>
            {mapping ? (
              <div style={{ padding: '8px 12px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 6, fontSize: 11, color: C.emerald, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  {mappingSource === 'saved' ? '💾 Remembered format' : '✓ Auto-detected'}:
                  X=<b>{headerName(mapping.x)}</b>, Y=<b>{headerName(mapping.y)}</b>, Z=<b>{mapping.z.map(headerName).join(', ')}</b>
                  {mapping.crs !== 'EPSG:4326' ? ` · ${mapping.crs}` : ''}
                </span>
                <button onClick={() => setShowMappingDialog(true)}
                  style={{ marginLeft: 'auto', fontSize: 10, padding: '3px 8px', borderRadius: 5, background: 'rgba(52,211,153,0.12)', color: C.emerald, border: '1px solid rgba(52,211,153,0.3)', cursor: 'pointer' }}>
                  Edit mapping
                </button>
              </div>
            ) : (
              <div style={{ padding: '8px 12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 6, fontSize: 11, color: C.amber, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>⚠ Columns not confidently detected ({((tabular.detection.confidence || 0) * 100).toFixed(0)}%) — Import opens the mapping editor</span>
                <button onClick={() => setShowMappingDialog(true)}
                  style={{ marginLeft: 'auto', fontSize: 10, padding: '3px 8px', borderRadius: 5, background: 'rgba(251,191,36,0.12)', color: C.amber, border: '1px solid rgba(251,191,36,0.3)', cursor: 'pointer' }}>
                  Map columns
                </button>
              </div>
            )}
          </div>
        )}

        {/* Survey QC (SuperSting .stg) */}
        {stgQc && (
          <div style={{ padding: '0 20px 12px' }}>
            <div style={{ padding: '8px 12px', background: C.bg4, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11, color: C.text1 }}>
              <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', marginBottom: 6 }}>Survey QC</div>
              {stgQc.loading && <div>Running QC…</div>}
              {stgQc.error && <div style={{ color: C.red }}>✗ {stgQc.error}</div>}
              {stgQc.result && (() => {
                const { summary: sm, keptD, keptCD, parsed } = stgQc.result;
                const kept = dropC ? keptCD : keptD;
                return (
                  <div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                      {['A', 'B', 'C', 'D'].map(t => (
                        <span key={t} style={{ color: TIER_COLORS[t], fontWeight: 600 }}>{t}: {sm.counts[t]}</span>
                      ))}
                      <span style={{ color: C.text3 }}>of {parsed} readings</span>
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      Remote electrode: {sm.remote.detected ? `detected (x = ${sm.remote.xs.join(', ')})` : 'none'}
                      {sm.recipPairs > 0 ? ` · ${sm.recipPairs} reciprocal pair${sm.recipPairs === 1 ? '' : 's'}` : ''}
                    </div>
                    {sm.warnings.map((w, i) => (
                      <div key={i} style={{ color: C.amber, marginBottom: 2 }}>⚠ {w}</div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.text2 }}>
                        <input type="checkbox" checked={dropC} onChange={e => setDropC(e.target.checked)} />
                        Also drop tier C
                      </label>
                      <button
                        onClick={() => downloadText(`${file.name.replace(/\.stg$/i, '')}_cleaned.stg`, dropC ? stgQc.result.cleaned.CD : stgQc.result.cleaned.D)}
                        style={{ marginLeft: 'auto', fontSize: 10, padding: '3px 8px', borderRadius: 5, background: 'rgba(52,211,153,0.12)', color: C.emerald, border: '1px solid rgba(52,211,153,0.3)', cursor: 'pointer' }}>
                        Download cleaned .stg ({kept} kept)
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div style={{ padding: '0 20px 12px' }}>
            <div style={{ padding: '8px 12px', background: 'rgba(16,185,129,0.1)', border: `1px solid rgba(16,185,129,0.25)`, borderRadius: 6, fontSize: 11, color: C.emerald }}>
              ✓ Imported {preview.count} features ({Object.entries(preview.types).map(([k, v]) => `${v} ${k}`).join(', ')})
              {preview.skipped && <div style={{ color: C.amber, marginTop: 4 }}>⚠ {preview.skipped}</div>}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: '0 20px 12px' }}>
            <div style={{ padding: '8px 12px', background: 'rgba(248,113,113,0.1)', border: `1px solid rgba(248,113,113,0.25)`, borderRadius: 6, fontSize: 11, color: C.red }}>
              ✗ {error}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg4, color: C.text1, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          {isStg && (
            <a href={`${process.env.PUBLIC_URL || "/gis"}/upload`} target="_blank" rel="noreferrer" style={{ alignSelf: 'center', fontSize: 11, color: C.accent }}>Go to Upload page</a>
          )}
          <button onClick={handleImport} disabled={!file || loading || isStg}
            title={isStg ? 'SuperSting .stg files are QC-only here. Upload them from the Upload page.' : undefined}
            style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: canImport ? 'linear-gradient(135deg,#00b8de,#3b82f6)' : C.bg4, color: canImport ? '#fff' : C.text3, fontWeight: 600, cursor: canImport ? 'pointer' : 'default', fontSize: 12 }}>
            {isStg ? 'QC only — upload .stg files from the Upload page' : loading ? (progress > 0 ? `Importing... ${Math.round(progress * 100)}%` : 'Importing...') : 'Import'}
          </button>
        </div>
      </div>

      {/* Column mapping editor */}
      {showMappingDialog && tabular && (
        <ColumnMappingDialog
          fileName={file?.name}
          headers={tabular.headers}
          rows={tabular.rows}
          detection={mapping ? { ...tabular.detection, x: mapping.x, y: mapping.y, z: mapping.z, crs: mapping.crs } : tabular.detection}
          initialError={mappingError}
          onApply={(m, built) => {
            setMapping(m);
            setMappingSource('manual');
            setMappingError(null);
            setShowMappingDialog(false);
            try { importWithMapping(m, built); } catch (err) { setError(err.message); }
          }}
          onClose={() => { setShowMappingDialog(false); setMappingError(null); }}
        />
      )}
    </div>
  );
}

function countGeometryTypes(geojson) {
  const counts = {};
  geojson.features.forEach(f => {
    const t = f.geometry?.type || 'Unknown';
    counts[t] = (counts[t] || 0) + 1;
  });
  return counts;
}
